import type { CreateUserInput } from '@chukua/common';
import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { useToast } from '../../lib/use-toast';
import { useResetPassword } from './use-reset-password';
import { useUserList } from './use-user-list';
import { createUser, deactivateUser, reactivateUser, setUserRoles, type UserListItem } from './users.api';

/**
 * The users list (`useUserList`) plus the mutating calls. `create` keeps its
 * error inline (a form failure); `setRoles`/`deactivate`/`reactivate` are row
 * actions with no form of their own, so their failures toast instead
 * (CLAUDE.md → Web conventions). Password reset is its own hook, `useResetPassword`.
 */
export function useUsers() {
  const { state, list, isRefreshing, reload } = useUserList();
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const resetPasswordState = useResetPassword();
  const toast = useToast();

  /** A row action with no form of its own: reload and toast on success, toast.error on failure (CLAUDE.md → Web conventions). */
  async function rowAction(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<void> {
    try {
      await action();
      await reload();
      toast.success(successMessage);
    } catch {
      toast.error(errorMessage);
    }
  }

  /** Returns whether it succeeded, so the drawer knows to close itself — same shape as useTotpEnrollment.disable. */
  async function create(input: CreateUserInput): Promise<boolean> {
    setCreateError(null);
    setCreating(true);
    try {
      await createUser(input);
      await reload();
      toast.success(`${input.name} created`);
      return true;
    } catch (err) {
      setCreateError(
        err instanceof ApiError && err.status === 409
          ? 'A user with this email already exists.'
          : 'Could not create the user — check the fields and try again.',
      );
      return false;
    } finally {
      setCreating(false);
    }
  }

  const setRoles = (user: UserListItem, roles: string[]) =>
    rowAction(() => setUserRoles(user.id, roles), `Updated roles for ${user.name}`, `Couldn't update roles for ${user.name}`);
  const deactivate = (user: UserListItem) => rowAction(() => deactivateUser(user.id), `${user.name} deactivated`, `Couldn't deactivate ${user.name}`);
  const reactivate = (user: UserListItem) => rowAction(() => reactivateUser(user.id), `${user.name} reactivated`, `Couldn't reactivate ${user.name}`);

  return {
    state,
    list,
    isRefreshing,
    createError,
    creating,
    create,
    resetCreateError: () => setCreateError(null),
    setRoles,
    deactivate,
    reactivate,
    ...resetPasswordState,
  };
}
